import {
  AlertTriangle,
  Fuel,
  Gauge,
  LayoutGrid,
  Navigation,
  ParkingCircle,
  Radio,
  Route,
  Timer,
  Users,
  Wallet,
  Wrench,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { DashboardTile, TileKey, WorstDriverEntry } from './api-types'

/**
 * How each tile is presented, and NOTHING about what it means.
 *
 * The value, the unit, the route, the direction and the pending reason all come from
 * the API — `dashboards.tiles.ts` says why the route in particular is server-side — so
 * what is left here is an icon and a catalogue key. That division is what lets the API
 * add a tile without a front-end release: an unrecognised key still renders, still
 * links, and says plainly that this build does not know its name rather than crashing
 * the page or silently dropping a figure somebody is looking for.
 *
 * The ORDER is fixed here rather than taken from the response. A dashboard whose tiles
 * move between refreshes because the server iterated a map in a different order is a
 * dashboard nobody builds muscle memory for, and the live counts belong first because
 * they are the only ones that answer "right now".
 */

export interface TilePresentation {
  readonly icon: LucideIcon
  /** Resolves `dashboard:tiles.<i18nKey>.label` and `.hint`. */
  readonly i18nKey: string
}

const PRESENTATIONS: Readonly<Record<TileKey, TilePresentation>> = {
  vehicles_moving: { icon: Navigation, i18nKey: 'vehiclesMoving' },
  vehicles_stopped: { icon: ParkingCircle, i18nKey: 'vehiclesStopped' },
  vehicles_offline: { icon: Radio, i18nKey: 'vehiclesOffline' },
  distance_today: { icon: Route, i18nKey: 'distanceToday' },
  engine_hours_today: { icon: Timer, i18nKey: 'engineHoursToday' },
  fuel_today: { icon: Fuel, i18nKey: 'fuelToday' },
  alerts_open: { icon: AlertTriangle, i18nKey: 'alertsOpen' },
  utilization_today: { icon: Gauge, i18nKey: 'utilizationToday' },
  drivers_worst_week: { icon: Users, i18nKey: 'driversWorstWeek' },
  fuel_cost_month: { icon: Wallet, i18nKey: 'fuelCostMonth' },
  maintenance_due_soon: { icon: Wrench, i18nKey: 'maintenanceDueSoon' },
}

export const TILE_ORDER: readonly TileKey[] = [
  'vehicles_moving',
  'vehicles_stopped',
  'vehicles_offline',
  'alerts_open',
  'distance_today',
  'engine_hours_today',
  'fuel_today',
  'utilization_today',
  'fuel_cost_month',
  'maintenance_due_soon',
  'drivers_worst_week',
]

export function presentationFor(key: string): TilePresentation | null {
  return (PRESENTATIONS as Readonly<Record<string, TilePresentation>>)[key] ?? null
}

/** The icon for a tile this build has never heard of. Neutral on purpose. */
export const UNKNOWN_TILE_ICON: LucideIcon = LayoutGrid

/**
 * The response's tiles in this module's order, with anything unrecognised appended
 * rather than dropped.
 *
 * Dropping would be the tidier code and the worse product: a tile the server built and
 * the client silently discarded is a figure that exists, that somebody was told about,
 * and that cannot be found on the page it belongs to.
 */
export function orderTiles(tiles: readonly DashboardTile[]): readonly DashboardTile[] {
  const byKey = new Map(tiles.map((tile) => [tile.key, tile]))
  const ordered: DashboardTile[] = []

  for (const key of TILE_ORDER) {
    const tile = byKey.get(key)
    if (tile !== undefined) {
      ordered.push(tile)
      byKey.delete(key)
    }
  }
  return [...ordered, ...byKey.values()]
}

/**
 * Which of the response's two windows a tile's comparison was computed over.
 *
 * The fleet home carries `today` AND `month` because its tiles do not agree about what
 * "previous" means: "km aujourd'hui" is compared against yesterday cut at the same
 * hour, "coût du mois" against the previous month cut at the same day. Labelling both
 * with one period would put "vs les 12 premiers jours" under a figure that covers nine
 * hours — a qualifier that is worse than none, because it is specific and wrong.
 *
 * Null means the tile has no period at all: the live counts are an instant, and alerts,
 * drivers and maintenance are a standing state rather than a flow.
 */
export function periodOfTile(key: string): 'today' | 'month' | null {
  switch (key) {
    case 'distance_today':
    case 'engine_hours_today':
    case 'fuel_today':
    case 'utilization_today':
      return 'today'
    case 'fuel_cost_month':
      return 'month'
    default:
      return null
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * `detail` readers
 *
 * `detail` is `Record<string, unknown>` on the wire, so every one of these validates
 * what it takes. A tile whose detail arrives in an unexpected shape renders WITHOUT
 * its breakdown rather than throwing — one malformed field must not take down the
 * eight tiles beside it, which is the same reasoning `Promise.allSettled` follows on
 * the server.
 * ──────────────────────────────────────────────────────────────────────────── */

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export interface SeverityBreakdown {
  readonly critical: number
  readonly warning: number
  readonly info: number
}

export function severityBreakdownOf(tile: DashboardTile): SeverityBreakdown | null {
  const detail = record(tile.detail)
  if (detail === null) return null
  const critical = count(detail['critical'])
  const warning = count(detail['warning'])
  const info = count(detail['info'])
  if (critical === null || warning === null || info === null) return null
  return { critical, warning, info }
}

export interface MaintenanceBreakdown {
  readonly overdue: number
  readonly warning: number
  readonly grounded: number
  /**
   * Plans no reading could answer — an hours-based vidange on an LV-CAN200 machine
   * that never reports AVL 102. The API keeps them OUT of the headline and so does
   * this screen: they are an installer's queue, not a mechanic's.
   */
  readonly cannotEvaluate: number
}

export function maintenanceBreakdownOf(tile: DashboardTile): MaintenanceBreakdown | null {
  const detail = record(tile.detail)
  if (detail === null) return null
  const overdue = count(detail['overdue'])
  const warning = count(detail['warning'])
  const grounded = count(detail['grounded'])
  const cannotEvaluate = count(detail['cannotEvaluate'])
  if (overdue === null || warning === null) return null
  return {
    overdue,
    warning,
    grounded: grounded ?? 0,
    cannotEvaluate: cannotEvaluate ?? 0,
  }
}

export function worstDriversOf(tile: DashboardTile): readonly WorstDriverEntry[] {
  const detail = record(tile.detail)
  const raw = detail?.['drivers']
  if (!Array.isArray(raw)) return []

  const entries: WorstDriverEntry[] = []
  for (const candidate of raw) {
    const row = record(candidate)
    if (row === null) continue
    const driverId = row['driverId']
    const driverName = row['driverName']
    const route = row['route']
    if (typeof driverId !== 'string' || typeof driverName !== 'string') continue
    entries.push({
      rank: count(row['rank']),
      driverId,
      driverName,
      score: count(row['score']),
      trend: typeof row['trend'] === 'string' ? row['trend'] : null,
      // A driver row with no route is still worth listing; it just is not a link.
      route: typeof route === 'string' ? route : '',
    })
  }
  return entries
}

export interface SegmentCounts {
  readonly vehicles: number
  readonly reportingVehicles: number
}

export function segmentCountsOf(tile: DashboardTile): SegmentCounts | null {
  const detail = record(tile.detail)
  if (detail === null) return null
  const vehicles = count(detail['vehicles'])
  const reportingVehicles = count(detail['reportingVehicles'])
  if (vehicles === null || reportingVehicles === null) return null
  return { vehicles, reportingVehicles }
}
